import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { FORGE_ATTACHMENT_ACTION_DISCOVERY_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import type { AttachmentActionSurface } from '../../types.ts';

type DiscoveryExperienceTracker = Pick<
	ExperienceTrackerAPI,
	'abort' | 'fail' | 'start' | 'succeed'
>;

type DiscoveryExperienceSurface = AttachmentActionSurface | 'mixed';

export type AttachmentActionDiscoveryAbortReason =
	| 'access-unavailable'
	| 'unmounted-before-settled';

export type AttachmentActionDiscoverySuccessReason = 'invalid-descriptor';

type ParticipantState =
	| { status: 'pending' }
	| { status: 'success'; reason?: AttachmentActionDiscoverySuccessReason }
	| { status: 'failure' }
	| { status: 'abort'; reason: AttachmentActionDiscoveryAbortReason };

type Participant = {
	registration: number;
	state: ParticipantState;
	surface: AttachmentActionSurface;
};

type DiscoveryWave = {
	completed: boolean;
	participants: Map<string, Participant>;
	settlementScheduled: boolean;
};

export type AttachmentActionDiscoveryExperienceLease = {
	abort: (reason: AttachmentActionDiscoveryAbortReason) => void;
	fail: () => void;
	succeed: (reason?: AttachmentActionDiscoverySuccessReason) => void;
};

const DISCOVERY_FAILURE_REASON = 'unexpected-error';
const DISCOVERY_FAILURE_MESSAGE = 'Attachment action discovery failed';

const ABORT_REASONS: Record<AttachmentActionDiscoveryAbortReason, string> = {
	'access-unavailable': 'Attachment action discovery access unavailable',
	'unmounted-before-settled': 'Attachment action discovery unmounted before settling',
};

class AttachmentActionDiscoveryExperienceError extends Error {
	constructor() {
		super(DISCOVERY_FAILURE_MESSAGE);
		this.name = 'AttachmentActionDiscoveryExperienceError';
	}
}

const getSurface = (wave: DiscoveryWave): DiscoveryExperienceSurface => {
	let surface: AttachmentActionSurface | undefined;

	for (const participant of wave.participants.values()) {
		if (surface && surface !== participant.surface) {
			return 'mixed';
		}

		surface = participant.surface;
	}

	return surface ?? 'mixed';
};

const getAttributes = (
	wave: DiscoveryWave,
	outcomeReason?:
		| AttachmentActionDiscoveryAbortReason
		| AttachmentActionDiscoverySuccessReason
		| typeof DISCOVERY_FAILURE_REASON,
) => ({
	attachmentActionLocation: 'metadata',
	surface: getSurface(wave),
	...(outcomeReason ? { outcomeReason } : {}),
});

/**
 * Coordinates simultaneous table-level discovery operations into one tracker lifecycle.
 *
 * ExperienceTracker addresses running experiences by name, so starting one task per Attachments
 * Macro would cause the tasks to replace one another. This coordinator keeps participant identity
 * local and publishes one bounded wave without changing the shared tracker contract.
 */
export class AttachmentActionDiscoveryExperienceCoordinator {
	private readonly waves = new WeakMap<DiscoveryExperienceTracker, DiscoveryWave>();

	register({
		participantId,
		surface,
		tracker,
	}: {
		participantId: string;
		surface: AttachmentActionSurface;
		tracker: DiscoveryExperienceTracker;
	}): AttachmentActionDiscoveryExperienceLease {
		let wave = this.waves.get(tracker);

		if (!wave) {
			wave = {
				completed: false,
				participants: new Map(),
				settlementScheduled: false,
			};
			this.waves.set(tracker, wave);
			tracker.start({
				name: FORGE_ATTACHMENT_ACTION_DISCOVERY_EXPERIENCE,
				attributes: {
					attachmentActionLocation: 'metadata',
					surface,
				},
			});
		}

		const registration = (wave.participants.get(participantId)?.registration ?? 0) + 1;
		wave.participants.set(participantId, {
			registration,
			state: { status: 'pending' },
			surface,
		});

		return {
			abort: (reason) => {
				this.updateParticipant({
					participantId,
					registration,
					state: { status: 'abort', reason },
					tracker,
					wave,
				});
			},
			fail: () => {
				this.updateParticipant({
					participantId,
					registration,
					state: { status: 'failure' },
					tracker,
					wave,
				});
			},
			succeed: (reason) => {
				this.updateParticipant({
					participantId,
					registration,
					state: { status: 'success', ...(reason ? { reason } : {}) },
					tracker,
					wave,
				});
			},
		};
	}

	private updateParticipant({
		participantId,
		registration,
		state,
		tracker,
		wave,
	}: {
		participantId: string;
		registration: number;
		state: ParticipantState;
		tracker: DiscoveryExperienceTracker;
		wave: DiscoveryWave;
	}): void {
		if (wave.completed || this.waves.get(tracker) !== wave) {
			return;
		}

		const participant = wave.participants.get(participantId);
		if (
			!participant ||
			participant.registration !== registration ||
			participant.state.status !== 'pending'
		) {
			return;
		}

		wave.participants.set(participantId, { ...participant, state });
		this.scheduleSettlement(tracker, wave);
	}

	private scheduleSettlement(tracker: DiscoveryExperienceTracker, wave: DiscoveryWave): void {
		if (wave.settlementScheduled) {
			return;
		}

		wave.settlementScheduled = true;
		void Promise.resolve().then(() => {
			wave.settlementScheduled = false;
			this.settle(tracker, wave);
		});
	}

	private settle(tracker: DiscoveryExperienceTracker, wave: DiscoveryWave): void {
		if (wave.completed || this.waves.get(tracker) !== wave) {
			return;
		}

		const participants = Array.from(wave.participants.values());
		const hasFailure = participants.some(({ state }) => state.status === 'failure');
		if (hasFailure) {
			this.complete(tracker, wave, () => {
				tracker.fail({
					name: FORGE_ATTACHMENT_ACTION_DISCOVERY_EXPERIENCE,
					error: new AttachmentActionDiscoveryExperienceError(),
					attributes: getAttributes(wave, DISCOVERY_FAILURE_REASON),
				});
			});
			return;
		}

		if (participants.some(({ state }) => state.status === 'pending')) {
			return;
		}

		const abortReason = participants.some(
			({ state }) => state.status === 'abort' && state.reason === 'access-unavailable',
		)
			? 'access-unavailable'
			: participants.some(({ state }) => state.status === 'abort')
				? 'unmounted-before-settled'
				: undefined;

		if (abortReason) {
			this.complete(tracker, wave, () => {
				tracker.abort({
					name: FORGE_ATTACHMENT_ACTION_DISCOVERY_EXPERIENCE,
					reason: ABORT_REASONS[abortReason],
					attributes: getAttributes(wave, abortReason),
				});
			});
			return;
		}

		const successReason = participants.some(
			({ state }) => state.status === 'success' && state.reason === 'invalid-descriptor',
		)
			? 'invalid-descriptor'
			: undefined;

		this.complete(tracker, wave, () => {
			tracker.succeed({
				name: FORGE_ATTACHMENT_ACTION_DISCOVERY_EXPERIENCE,
				attributes: getAttributes(wave, successReason),
			});
		});
	}

	private complete(
		tracker: DiscoveryExperienceTracker,
		wave: DiscoveryWave,
		stop: () => void,
	): void {
		wave.completed = true;
		this.waves.delete(tracker);
		stop();
	}
}
